import React, { useMemo } from "react"

import { css } from "@emotion/react"

import { NIRVANA_COLORS } from "../config/theme.config.ts"
import { useIsMobile } from "../hooks/isMobile.hook"
import { PrimaryText } from "./primaryText.element"

import logo from "@front-shared/assets/images/logo-circle.svg"

export const NirvanaBreathingLoaderElement: React.FunctionComponent<
    INirvanaBreathingLoaderProps
> = props => {
    const isMobile = useIsMobile()

    const baseZIndex = useMemo<number>(
        () => props.baseZIndex || 300,
        [props.baseZIndex],
    )

    return props.isActive ? (
        <div className="relative">
            {/*ANIMATIONS*/}
            <style>
                {`
                    @keyframes breathing-loader-outer-circle {
                      0% {width: 188px; height: 188px; border: 2px solid ${NIRVANA_COLORS.brand.primary};}
                      33% {width: 303px; height: 303px; border: 2px solid ${NIRVANA_COLORS.brand.primary};}
                      40% {border: 2px solid rgba(44, 31, 69, 0.24);}
                      55% {width: 330px; height: 330px; border: 1px solid rgba(44, 31, 69, 0.24);}
                      75% {border: 2px solid rgba(44, 31, 69, 0.24);}
                      100% {width: 188px; height: 188px; border: 2px solid ${NIRVANA_COLORS.brand.primary};}
                    }

                    @keyframes breathing-loader-inner-circle {
                      0% {width: 181px; height: 181px;}
                      55% {width: 95px; height: 95px;}
                      100% {width: 181px; height: 181px;}
                    }

                    @keyframes breathing-loader-outer-shadow {
                      0% {top: 356px; opacity: 1;}
                      33% {top: 0px;opacity: 0.7;}
                      55% {top: 0px;opacity: 0.4;}
                      90% {top: 356px;opacity: 1;}
                      100% {top: 356px;opacity: 0;}
                    }
                `}
            </style>

            {/*OVERLAY*/}
            <div
                className="fixed"
                css={[
                    css({
                        zIndex: baseZIndex,
                    }),
                    styles.overlay,
                ]}
            />

            {/*LOADER*/}
            <div
                className={`
                    fixed
                    ${!isMobile ? "horizontal-center" : ""}
                `}
                css={[
                    css({
                        zIndex: baseZIndex + 1,
                    }),
                    styles.loaderWrapper,

                    isMobile
                        ? css({
                              left: "50%",
                              transform: "translateX(-50%) scale(0.8)",
                          })
                        : undefined,
                ]}
            >
                {/*breathing-loader-outer-circle*/}
                <div
                    className="absolute screen-center rounded-full"
                    css={[
                        css({
                            zIndex: baseZIndex + 3,
                        }),
                        styles.outerCircle,
                    ]}
                />

                {/*breathing-loader-inner-circle*/}
                <div
                    className="absolute screen-center rounded-full"
                    css={[
                        css({
                            zIndex: baseZIndex + 4,
                        }),
                        styles.innerCircle,
                    ]}
                />

                {/*breathing-loader-outer-shadow*/}
                <div
                    className="absolute screen-center rounded-full"
                    css={[
                        css({
                            zIndex: baseZIndex + 1,
                        }),
                        styles.outerShadowWrapper,
                    ]}
                >
                    <div className="relative" css={[styles.outerShadow]} />
                </div>

                {/*breathing-loader-logo*/}
                <div
                    className="absolute screen-center"
                    css={[
                        css({
                            zIndex: baseZIndex + 5,
                        }),
                        styles.logoWrapper,
                    ]}
                >
                    <img src={logo} alt="Nirvana" css={styles.logo} />
                </div>
            </div>

            {/*BREATHING TEXT*/}
            {!props.ignoreText && (
                <div
                    className="fixed horizontal-center"
                    css={[
                        css({
                            zIndex: baseZIndex + 1,
                            top: isMobile ? 475 : 500,
                            width: isMobile ? "91vw" : undefined,
                        }),
                    ]}
                >
                    <PrimaryText centered typography="h4">
                        Breathe in deeply through your nose for 3 seconds,
                        <br />
                        hold for 2 seconds, and exhale for
                        <br />4 seconds through your mouth...
                    </PrimaryText>
                </div>
            )}
        </div>
    ) : null
}

const styles = {
    overlay: css({
        left: 0,
        top: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: NIRVANA_COLORS.brand.offWhite,
        opacity: 0.8,
    }),
    loaderWrapper: css({
        top: 300,
    }),
    outerCircle: css({
        backgroundColor: NIRVANA_COLORS.brand.lilacLight,
        animationName: "breathing-loader-outer-circle",
        animationDuration: "9s",
        animationIterationCount: "infinite",
    }),
    innerCircle: css({
        backgroundColor: "white",
        animationName: "breathing-loader-inner-circle",
        animationDuration: "9s",
        animationIterationCount: "infinite",
    }),
    outerShadowWrapper: css({
        background: "transparent",
        width: 356,
        height: 356,
        overflow: "hidden",
    }),
    outerShadow: css({
        background:
            "linear-gradient(180deg, rgba(225, 223, 244, 0.7) 0%, rgba(225, 223, 244, 0) 100%)",
        width: 356,
        height: 356,
        borderBottomLeftRadius: 777,
        borderBottomRightRadius: 777,
        animationName: "breathing-loader-outer-shadow",
        animationDuration: "9s",
        animationIterationCount: "infinite",
    }),
    logoWrapper: css({
        transformOrigin: "left top",
        width: 66,
        height: 66,
    }),
    logo: css({
        width: "100%",
        height: "auto",
    }),
}

interface INirvanaBreathingLoaderProps {
    className?: string
    isActive?: boolean
    ignoreText?: boolean
    baseZIndex?: number
}
